<template>
    <v-app v-if="isLoading">
        <div class="loading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </div>
    </v-app>
    <v-app v-else>
        <v-navigation-drawer app v-model="drawer" temporary v-if="user">
            <v-list nav>
                <img class="menuTitle" src="/gecko-logo-blue.svg" />
                <v-list-item prepend-icon="mdi-home" title="Home" color="primary" :to="{ name: 'home' }"></v-list-item>
                <v-list-item prepend-icon="mdi-application" title="Gecko App" color="primary" :to="{ name: 'GeckoApp' }"></v-list-item>
                <v-list-item prepend-icon="mdi-beaker-outline" title="Account Chemicals" color="primary" :to="{ name: 'AccountChemicals' }"></v-list-item>
                <v-list-item prepend-icon="mdi-beaker-outline" title="System Chemicals" color="primary" :to="{ name: 'AllSystemChemicals' }"></v-list-item>
                <v-list-item prepend-icon="mdi-led-strip" title="Recent Test Strip Scans" color="primary" :to="{ name: 'RecentTestStrips' }"></v-list-item>
                <v-list-item prepend-icon="mdi-view-grid" title="Test Strip Catalog" color="primary" :to="{ name: 'TestStripCatalog' }"></v-list-item>
                <v-list-item prepend-icon="mdi-chart-bar" title="Test Strip Analytics" color="primary" :to="{ name: 'TestStripAnalytics' }"></v-list-item>
                <v-list-item prepend-icon="mdi-star" title="Recent Feedback" color="primary" :to="{ name: 'RecentFeedback' }"></v-list-item>

                <v-list-item prepend-icon="mdi-tag-multiple" title="Tags" color="primary" :to="{ name: 'Tags' }"></v-list-item>
                <v-list-item prepend-icon="mdi-help-circle-outline" title="Help Center" color="primary" :to="{ name: 'HelpCenter' }"></v-list-item>
                <v-list-item-subtitle class="mt-5 mb-2">Engineering</v-list-item-subtitle>
                <v-list-item
                    prepend-icon="mdi-shovel"
                    title="Vessel Status Shoveler"
                    color="primary"
                    :to="{ name: 'VesselStatusUpdateShoveler' }"
                ></v-list-item>
                <v-list-item prepend-icon="mdi-cloud-print" title="CloudWatch Logs" color="primary" :to="{ name: 'CloudWatch' }"></v-list-item>
            </v-list>
        </v-navigation-drawer>

        <AppNavbar @clicked-hamburger="drawer = !drawer" @clicked-search="showSearchDialog = true" />

        <v-main :style="{ 'background-color': backgroundColor }">
            <router-view></router-view>
        </v-main>

        <v-footer class="bg-grey-darken-3 compact-footer">
            <v-container class="py-2">
                <v-row class="text-center" no-gutters>
                    <v-col class="text-white copy">©{{ new Date().getFullYear() }} Gecko Alliance</v-col>
                </v-row>
            </v-container>
        </v-footer>

        <Toast />

        <v-dialog v-model="showGlobalError" max-width="1000">
            <v-card>
                <v-card-text>
                    <v-alert type="error" icon="mdi-alert" title="Oops!">{{ globalErrorMessage }}</v-alert>
                </v-card-text>
                <template v-slot:actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red-darken-2" @click="closeErrorDialog" variant="flat">OK</v-btn>
                    <v-spacer></v-spacer>
                </template>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showSearchDialog" retain-focus class="searchDialog" min-width="300" max-width="600">
            <v-card>
                <v-card-text>
                    <v-text-field
                        autofocus
                        v-model="searchTerm"
                        prepend-inner-icon="mdi-magnify"
                        :loading="loadingSearchResults"
                        label="Search"
                        placeholder="Search"
                        clearable
                        :hint="searchHint"
                    ></v-text-field>

                    <div>
                        <v-list lines="two" style="font-size: 1.2rem">
                            <div v-if="accountSearchResults && accountSearchResults.length" class="mb-4">
                                <v-divider></v-divider>
                                <v-list-subheader>Accounts</v-list-subheader>
                                <v-list-item
                                    v-for="result of accountSearchResults"
                                    :key="result.accountId"
                                    :value="result"
                                    @click="navigateToAccount(result)"
                                    color="primary"
                                    rounded="xl"
                                    append-icon="mdi-chevron-right"
                                >
                                    <template v-if="user?.picture" v-slot:prepend>
                                        <v-avatar size="36px">
                                            <img :src="user.picture" referrerpolicy="no-referrer" />
                                        </v-avatar>
                                    </template>
                                    <v-list-item-title><span v-html="searchTermHighlightedHtml(userName(result))"></span></v-list-item-title>
                                    <v-list-item-subtitle
                                        ><span v-html="searchTermHighlightedHtml(`Account #${result.accountId}, ${result.email}`)"></span
                                    ></v-list-item-subtitle>
                                </v-list-item>
                            </div>
                            <div v-if="vesselSearchResults && vesselSearchResults.length">
                                <v-divider></v-divider>
                                <v-list-subheader>Vessels</v-list-subheader>
                                <VesselSearchResult
                                    v-for="result of vesselSearchResults"
                                    :key="result.vesselId"
                                    :vessel="result"
                                    :searchTerm="searchTerm"
                                    @click="navigateToVessel(result)"
                                />
                            </div>
                            <div v-if="monitorSearchResults && monitorSearchResults.length">
                                <v-divider></v-divider>
                                <v-list-subheader>Monitors</v-list-subheader>
                                <v-list-item
                                    v-for="result of monitorSearchResults"
                                    :key="result.monitorId"
                                    :value="result"
                                    @click="navigateToMonitor(result)"
                                    color="primary"
                                    rounded="xl"
                                    append-icon="mdi-chevron-right"
                                >
                                    <v-list-item-title><span v-html="searchTermHighlightedHtml(result.monitorId)"></span></v-list-item-title>
                                    <v-list-item-subtitle>
                                        <span
                                            v-if="result.vesselAccountId ?? result.owningAccountId"
                                            v-html="searchTermHighlightedHtml(`Account #${result.vesselAccountId ?? result.owningAccountId}`)"
                                        />
                                        <span v-if="result.sensorSerialNumber && (result.vesselAccountId ?? result.owningAccountId)">, </span>
                                        <span v-if="result.sensorSerialNumber" v-html="searchTermHighlightedHtml(`Sensor #${result.sensorSerialNumber}`)" />
                                    </v-list-item-subtitle>
                                </v-list-item>
                            </div>
                            <div v-if="sensorSearchResults && sensorSearchResults.length">
                                <v-divider></v-divider>
                                <v-list-subheader>Sensors</v-list-subheader>
                                <v-list-item
                                    v-for="result of sensorSearchResults"
                                    :key="result.serialNumber"
                                    :value="result"
                                    @click="navigateToSensor(result)"
                                    color="primary"
                                    rounded="xl"
                                    append-icon="mdi-chevron-right"
                                >
                                    <v-list-item-title><span v-html="searchTermHighlightedHtml(result.serialNumber)"></span></v-list-item-title>
                                </v-list-item>
                            </div>
                        </v-list>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script lang="ts" setup>
import { useAuth0 } from '@auth0/auth0-vue'
import type { AccountSearchResultV1, MonitorSearchResultV1, SensorSearchResultV1, VesselSearchResultV1 } from '@geckoal/gecko-api-client'
import { debounce } from '@geckoal/gecko-webapp-utils'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useApi } from './api'
import AppNavbar from './components/AppNavbar.vue'
import './components/vesselCards/vesselCards.scss'
import VesselSearchResult from './components/vessels/VesselSearchResult.vue'
import Toast from 'primevue/toast'
import { toHTMLWithSearchTermHighlighted } from './lib/utils'
import { useGeneralStore } from './stores/general'

const generalStore = useGeneralStore()
const router = useRouter()
const auth = useAuth0()

const { isLoading } = auth
const user = auth.user

const drawer = ref(false)

const searchTerm = ref<string | undefined>(undefined)
const accountSearchResults = ref<AccountSearchResultV1[] | undefined>(undefined)
const vesselSearchResults = ref<VesselSearchResultV1[] | undefined>(undefined)
const monitorSearchResults = ref<MonitorSearchResultV1[] | undefined>(undefined)
const sensorSearchResults = ref<SensorSearchResultV1[] | undefined>(undefined)
const loadingSearchResults = ref(false)
const showSearchDialog = ref(false)

const escapeListener = (event: KeyboardEvent) => {
    if (event.key === 's' && event.ctrlKey) {
        showSearchDialog.value = true
    }
}

onMounted(() => {
    window.addEventListener('keydown', escapeListener)
})

onUnmounted(() => {
    window.removeEventListener('keydown', escapeListener)
})

const canUseSearchTerm = computed(() => {
    if (!searchTerm.value) return false
    if (Number.isInteger(parseInt(searchTerm.value, 10))) return true // ID search
    return searchTerm.value.length > 2
})

watch(
    searchTerm,
    debounce(() => {
        if (!canUseSearchTerm.value) {
            accountSearchResults.value = undefined
            vesselSearchResults.value = undefined
            return
        }
        if (searchTerm.value) search(searchTerm.value)
    }, 300)
)

watch(showSearchDialog, () => {
    if (!showSearchDialog.value) searchTerm.value = undefined // Clear last search when redisplaying the search dialog
})

const backgroundColor = computed(() => {
    return generalStore.$state.backgroundColor
})

const showNoResults = computed(() => {
    if (!searchTerm.value || searchTerm.value.length <= 2) return false // don't expect results
    if (loadingSearchResults.value === true) return false // wait until loading finishes

    const accountResultCount = accountSearchResults.value?.length ?? 0
    const vesselResultCount = vesselSearchResults.value?.length ?? 0
    const monitorResultCount = monitorSearchResults.value?.length ?? 0
    const sensorResultCount = sensorSearchResults.value?.length ?? 0

    return accountResultCount === 0 && vesselResultCount === 0 && monitorResultCount === 0 && sensorResultCount === 0
})

const searchHint = computed(() => {
    if (!searchTerm.value || !searchTerm.value.length) return 'Start typing to search for accounts, vessels, monitors, and sensors'
    if (Number.isInteger(parseInt(searchTerm.value, 10))) return undefined // valid condition when searching for a vessel id
    if (searchTerm.value.length === 1) return 'Just a couple more characters'
    if (searchTerm.value.length === 2) return 'Just one more character'
    if (loadingSearchResults.value === true) return `Searching for '${searchTerm.value}'`
    if (showNoResults.value === true) return `No results for '${searchTerm.value}'`
    return undefined
})

const navigateToAccount = (account: AccountSearchResultV1) => {
    router.replace({ name: 'AccountDetails', params: { accountId: account.accountId } })
    showSearchDialog.value = false
}

const navigateToVessel = (vessel: VesselSearchResultV1) => {
    router.replace({ name: 'VesselDetails', params: { accountId: vessel.accountId, vesselId: vessel.vesselId } })
    showSearchDialog.value = false
}

const navigateToMonitor = (monitor: MonitorSearchResultV1) => {
    router.replace({ name: 'MonitorDetails', params: { monitorId: monitor.monitorId } })
    showSearchDialog.value = false
}

const navigateToSensor = (sensor: SensorSearchResultV1) => {
    router.replace({ name: 'SensorDetails', params: { serialNumber: sensor.serialNumber } })
    showSearchDialog.value = false
}

const search = async (searchTerm: string) => {
    if (!canUseSearchTerm.value) return
    try {
        loadingSearchResults.value = true
        const result = await useApi().searchAccounts({
            searchAccountsV1: {
                searchTerm
            }
        })
        accountSearchResults.value = result.accounts
        vesselSearchResults.value = result.vessels
        monitorSearchResults.value = result.monitors
        sensorSearchResults.value = result.sensors
    } finally {
        loadingSearchResults.value = false
    }
}

const globalErrorMessage = computed(() => {
    return generalStore.globalErrorMessage
})

const showGlobalError = computed(() => {
    return !!globalErrorMessage.value
})

const closeErrorDialog = () => {
    generalStore.$patch({
        globalErrorMessage: undefined
    })
}

const searchTermHighlightedHtml = (text: string | undefined) => {
    return toHTMLWithSearchTermHighlighted(text, searchTerm.value)
}

const userName = (result: AccountSearchResultV1) => {
    let name = ''
    if (result.firstName !== undefined) name += result.firstName
    if (result.firstName !== undefined && result.lastName !== undefined) name += ' '
    if (result.lastName !== undefined) name += result.lastName
    return name
}
</script>

<style lang="scss" scoped>
.copy {
    font-weight: 300;
}

/* Use higher specificity to override Vuetify default styles without !important */
.v-application .v-app-bar + .v-main + .v-footer.compact-footer,
.v-application .v-footer.compact-footer.bg-grey-darken-3 {
    min-height: auto;
    height: auto;
    padding: 0;
    flex: none;
}

.menuTitle {
    width: 100px;
    display: block;
    margin: 10px auto 30px auto;
}

.loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
